import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { sidebarNavIconSlotClass } from "../../config/nav";
import { authService } from "../../services/auth";
import { persistSidebarCollapsed } from "./sidebarStorage";

/**
 * Pop-out menu (Moonscroll-style) for sidebar footer: collapse/expand + log out.
 */
export default function MoreMenu({ collapsed, variant, onCollapsedChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();

  const showSidebarToggle = variant === "desktop";

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handlePointerDown);
    }
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    authService.logout();
  };

  const handleToggleSidebar = () => {
    const next = !collapsed;
    onCollapsedChange(next);
    persistSidebarCollapsed(next);
    setIsOpen(false);
  };

  return (
    <div className="relative overflow-visible">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={collapsed && variant === "desktop" ? "More" : undefined}
        className={`w-full flex items-center gap-3 rounded px-3 py-2.5 font-mono text-[12px] tracking-[0.04em] uppercase transition-colors text-theme-muted hover:bg-theme-panel hover:text-theme-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main ${
          collapsed && variant === "desktop" ? "md:justify-center md:px-2" : ""
        }`}
      >
        <span className={sidebarNavIconSlotClass}>
          <span className="material-symbols-outlined text-[18px] leading-none">more_horiz</span>
        </span>
        <span className={`truncate ${collapsed && variant === "desktop" ? "md:hidden" : ""}`}>More</span>
      </button>

      {isOpen ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute bottom-full left-0 z-[100] mb-2 w-52 rounded-lg border border-theme-border bg-theme-panel py-1 shadow-xl"
        >
          {showSidebarToggle ? (
            <button
              type="button"
              role="menuitem"
              onClick={handleToggleSidebar}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left font-mono text-[12px] text-theme-text transition-colors hover:bg-theme-main"
            >
              <span className="material-symbols-outlined text-[18px] text-theme-muted shrink-0">
                {collapsed ? "keyboard_double_arrow_right" : "keyboard_double_arrow_left"}
              </span>
              {collapsed ? "Expand sidebar" : "Collapse sidebar"}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left font-mono text-[12px] text-theme-text transition-colors hover:bg-theme-main"
          >
            <span className="material-symbols-outlined text-[18px] text-theme-muted shrink-0">logout</span>
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}
